import { useEffect, useRef } from "react";
import { ConnectProto, PickupLocationType } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { splitAvailabilitySchedule } from "~/shared/lib/availability-schedule";
import type { PickupWindow } from "../model/pickup-window";

type JobType = ConnectProto.JobType;

/**
 * Default time window of a new job: today, from now until the end of the day.
 * Start is "now" rather than 00:00 so it never lands on an already-past slot
 * (past days aren't pickable).
 */
const getTodayStartDate = (): Date => new Date();

const getTodayEndDate = (): Date => {
  const end = new Date();
  end.setHours(23, 59, 0, 0);
  return end;
};

export const getTodayWindow = (): PickupWindow => ({
  from: getTodayStartDate(),
  to: getTodayEndDate(),
});

const parseDateField = (field: any): Date | undefined => {
  if (!field) return undefined;
  if (field instanceof Date) return field;
  if (typeof field === "object" && "seconds" in field) {
    return fromTimestamp(field) || undefined;
  }
  return new Date(field);
};

const mapPickupLocationType = (
  type: ConnectProto.PickupLocationType | PickupLocationType,
): PickupLocationType => {
  if (
    type === ConnectProto.PickupLocationType.CONSTRUCTION ||
    type === PickupLocationType.PICKUP_LOCATION_TYPE_CONSTRUCTION
  ) {
    return PickupLocationType.PICKUP_LOCATION_TYPE_CONSTRUCTION;
  }
  if (
    type === ConnectProto.PickupLocationType.HOUSEHOLD ||
    type === PickupLocationType.PICKUP_LOCATION_TYPE_HOUSEHOLD
  ) {
    return PickupLocationType.PICKUP_LOCATION_TYPE_HOUSEHOLD;
  }
  return PickupLocationType.PICKUP_LOCATION_TYPE_UNDEFINED;
};

export const mapJobToCamelCase = (job: any) => {
  if (!job) return null;
  return {
    ...job,
    id: job.id !== undefined ? BigInt(job.id) : undefined,
    pickupLocationId:
      job.pickupLocationId ??
      (job.pickup_location_id !== undefined ? BigInt(job.pickup_location_id) : undefined),
    pickupLocation: job.pickupLocation ?? job.pickup_location,
    containerTypeId:
      job.containerTypeId ??
      (job.container_type_id !== undefined ? BigInt(job.container_type_id) : undefined),
    containerGroupId:
      job.containerGroupId ??
      (job.container_group_id !== undefined ? BigInt(job.container_group_id) : undefined),
    containerGroup: job.containerGroup ?? job.container_group,
    driverComment: job.driverComment ?? job.driver_comment,
    jobTariff: job.jobTariff ?? (job.job_tariff !== undefined ? BigInt(job.job_tariff) : undefined),
    visibleToAi: job.visibleToAi ?? job.visible_to_ai,
    twStartAt: parseDateField(job.twStartAt ?? job.tw_start_at),
    twEndAt: parseDateField(job.twEndAt ?? job.tw_end_at),
    type: job.type,
    jobCategory: job.jobCategory ?? job.job_category,
    serviceProviderId:
      job.serviceProviderId ??
      (job.service_provider_id !== undefined ? BigInt(job.service_provider_id) : undefined),
    serviceReceiverId:
      job.serviceReceiverId ??
      (job.service_receiver_id !== undefined ? BigInt(job.service_receiver_id) : undefined),
    fileReports: job.fileReports ?? job.file_reports ?? [],
    numberOfContainers: job.numberOfContainers ?? job.number_of_containers,
  };
};

type MappedJob = ReturnType<typeof mapJobToCamelCase>;

type SelectedLocation = {
  id: string;
  name: string;
  tariff?: number;
  type?: PickupLocationType;
  startTime?: string;
  endTime?: string;
  ownerOrganizationId?: bigint;
} | null;

type SelectedJobType = { value: JobType; label: string } | null;
type SelectedReceiver = { id: string; name: string } | null;

type LocationItem = {
  id: string;
  name: string;
  tariff?: number;
  type: PickupLocationType;
  availabilitySchedule?: string;
  availability_schedule?: string;
  ownerOrganizationId?: bigint;
};

type ReceiverItem = { id: bigint | string | number; name: string };

type UseCreateOrderPreselectParams = {
  isEditMode: boolean;
  mappedJob: MappedJob;
  mappedJobTypeNum: ConnectProto.JobType;
  locations: LocationItem[];
  receivers: ReceiverItem[];
  editReceiverById: ReceiverItem | undefined;
  containerGroups: any[];
  preselectedLocationId?: string;
  preselectedLocationName?: string;
  preselectedLocationTariff?: number;
  jobTypes: { value: JobType; label: string }[];
  setPickupLocationId: (id: string) => void;
  setSelectedLocation: (loc: SelectedLocation) => void;
  setSelectedJobType: (type: SelectedJobType) => void;
  setSelectedReceiver: (rec: SelectedReceiver) => void;
  setSelectedContainerTypeId: (id: string | null) => void;
  setSelectedContainerGroupId: (id: string | null) => void;
  setComment: (c: string) => void;
  setHideFromAi: (hide: boolean) => void;
  setTwWindow: (window: PickupWindow) => void;
};

export function useCreateOrderPreselect({
  isEditMode,
  mappedJob,
  mappedJobTypeNum,
  locations,
  receivers,
  editReceiverById,
  containerGroups,
  preselectedLocationId,
  preselectedLocationName,
  preselectedLocationTariff,
  jobTypes,
  setPickupLocationId,
  setSelectedLocation,
  setSelectedJobType,
  setSelectedReceiver,
  setSelectedContainerTypeId,
  setSelectedContainerGroupId,
  setComment,
  setHideFromAi,
  setTwWindow,
}: UseCreateOrderPreselectParams) {
  // Edit mode: one effect that waits for both locations and containerGroups,
  // then sets everything at once. Re-runs while data loads, until the groups are in; after that
  // only a new job version re-applies it, so a list refetched on a pick keeps the edits.
  const appliedJob = useRef<typeof mappedJob>(undefined);
  useEffect(() => {
    if (!isEditMode || !mappedJob || appliedJob.current === mappedJob) return;

    // basic fields
    setPickupLocationId(
      mappedJob.pickupLocationId?.toString() || mappedJob.pickupLocation?.id?.toString() || "",
    );
    setComment(mappedJob.driverComment || "");
    setHideFromAi(mappedJob.visibleToAi === false);

    // time window
    setTwWindow({
      from: mappedJob.twStartAt ?? getTodayStartDate(),
      to: mappedJob.twEndAt ?? getTodayEndDate(),
    });

    // job type
    const typeOption = jobTypes.find((t) => t.value === mappedJobTypeNum);
    if (typeOption) setSelectedJobType(typeOption);

    // location: prefer list match, fallback to embedded
    const locationFromList = locations.find((loc) => BigInt(loc.id) === mappedJob.pickupLocationId);
    if (locationFromList) {
      const [startTime, endTime] = splitAvailabilitySchedule(locationFromList.availabilitySchedule);
      setSelectedLocation({
        id: locationFromList.id.toString(),
        name: locationFromList.name,
        tariff: locationFromList.tariff,
        type: locationFromList.type,
        startTime,
        endTime,
        ownerOrganizationId: locationFromList.ownerOrganizationId,
      });
    } else if (mappedJob.pickupLocation?.id) {
      const embedded = mappedJob.pickupLocation;
      const [startTime, endTime] = splitAvailabilitySchedule(embedded.availabilitySchedule);
      setSelectedLocation({
        id: embedded.id.toString(),
        name: embedded.name,
        tariff: embedded.tariff != null ? Number(embedded.tariff) : undefined,
        type: embedded.type ? mapPickupLocationType(embedded.type) : undefined,
        startTime,
        endTime,
        ownerOrganizationId: embedded.ownerOrganizationId ?? embedded.owner_organization_id,
      });
    }

    // container group: only when groups are loaded; a group on no list is not waited for
    if (containerGroups.length === 0) return;
    appliedJob.current = mappedJob;

    const targetGroupId =
      mappedJob.containerGroup?.id?.toString() ?? mappedJob.containerGroupId?.toString() ?? null;

    const matchedGroup =
      (targetGroupId && containerGroups.find((cg) => cg.id.toString() === targetGroupId)) ||
      (mappedJob.containerTypeId &&
        containerGroups.find((cg) => cg.containerType?.id === mappedJob.containerTypeId));

    if (!matchedGroup) return;

    setSelectedContainerGroupId(matchedGroup.id.toString());
    if (matchedGroup.containerType?.id) {
      setSelectedContainerTypeId(matchedGroup.containerType.id.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedJob, locations, containerGroups]);

  // Preselected location mode (create with prefilled location)
  useEffect(() => {
    if (
      isEditMode ||
      !preselectedLocationId ||
      !preselectedLocationName ||
      preselectedLocationTariff === undefined ||
      locations.length === 0
    )
      return;

    const location = locations.find((loc) => loc.id === preselectedLocationId);
    setPickupLocationId(preselectedLocationId);
    setSelectedLocation({
      id: preselectedLocationId,
      name: preselectedLocationName,
      tariff: preselectedLocationTariff,
      type: location?.type,
      ownerOrganizationId: location?.ownerOrganizationId,
    });
    setHideFromAi(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, isEditMode]);

  // Receiver preselect
  useEffect(() => {
    if (!isEditMode || !mappedJob?.serviceReceiverId) return;

    const fromList = receivers.find((r) => BigInt(r.id) === mappedJob.serviceReceiverId);
    if (fromList) {
      setSelectedReceiver({ id: fromList.id.toString(), name: fromList.name });
      return;
    }
    if (editReceiverById) {
      setSelectedReceiver({ id: editReceiverById.id.toString(), name: editReceiverById.name });
    }
  }, [receivers, mappedJob, isEditMode, editReceiverById]);
}
